import React, { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react'
import ResizeHandler from 'components/utils/ResizeHandler'
import isEqual from 'lodash/isEqual'

import each from 'lodash/each'
import cn from 'classnames'
import { FaThumbtack } from 'react-icons/fa'
import ContentEditable from 'react-contenteditable'
import { isEmbeddedReadOnly } from 'lib/embed'
import Permissions from 'components/permissions/Permissions'
import FooterMeta from './footerMeta'
import type { KindMap } from 'types/domain'

import 'components/graph/depth.less'
import 'components/graph/object.less'

type PermissionUser = {
  id: string
  username?: string
  index?: number
}

type GraphData = {
  content: string
  creator?: unknown
  writers?: unknown
  readers?: unknown
  kind?: string
  directionality?: string
}

type GraphEntity = {
  id: string
  data: GraphData
  focused?: boolean
  pinned?: boolean
  selected?: boolean
  editing?: boolean
  depth?: number
  modified?: number | null
  from?: string
  to?: string
  more?: number
  isMore?: boolean
  isMoreFor?: string
  preserved?: boolean
  initialPosition?: { x: number; y: number }
}

type LayoutAdapter = {
  add: (objectType: string, payload: Record<string, unknown>) => void
  remove: (objectType: string, id: string) => void
  setSize: (objectType: string, id: string, width: number, height: number) => void
  update: (objectType: string, payload: Record<string, unknown>) => void
  setDragOffset: (x: number, y: number) => void
}

type DragEventShape = React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>

export type GraphObjectProps = {
  object: GraphEntity
  layout: LayoutAdapter
  objectType: string
  objectAttrs: string[]
  showMetaDepth?: number
  startDragging: (payload: { id: string }) => void
  stopDragging: () => void
  openObjectDetails: () => void
  closeObjectDetails: (save: boolean) => void
  setNewContentPermissions: (payload: {
    readers: Record<string, PermissionUser>
    writers: Record<string, PermissionUser>
  }) => void
  edit: (payload: Record<string, unknown>) => void
  editing: {
    isEditing: boolean
    save: boolean
  }
  objectDetails: {
    isOpen: boolean
  }
  permissionsVisible: boolean
  focusOne: (payload: { id: string }) => void
  isDragging: boolean
  isCreating?: boolean
  isDropTarget?: boolean
  updateMouse: (event: DragEventShape) => void
  toggleSelectFocus: () => void
  me: { id?: string } | undefined
  kinds?: KindMap
  editOneKind?: (payload: { id: string; label: string; styles: Record<string, unknown> }) => void
  createOneKind?: (payload: { id: string; label: string; styles: { color: string } }) => void
}

function toPermissionUser(value: unknown): PermissionUser | undefined {
  if (!value || typeof value !== 'object') {
    return undefined
  }
  const id = (value as { id?: unknown }).id
  if (typeof id !== 'string' || id === '') {
    return undefined
  }
  return value as PermissionUser
}

function toPermissionRecord(value: unknown): Record<string, PermissionUser> | undefined {
  if (!value || typeof value !== 'object') {
    return undefined
  }
  const normalized: Record<string, PermissionUser> = {}
  each(value as Record<string, unknown>, (item: unknown, key: string) => {
    const user = toPermissionUser(item)
    if (user) {
      normalized[key] = user
    }
  })
  return Object.keys(normalized).length ? normalized : undefined
}

function getKindStyles(kindId: string | undefined, kinds: KindMap | undefined): React.CSSProperties {
  const kindStyle: React.CSSProperties = {}
  const kind = kindId ? kinds?.[kindId] : undefined
  if (!kind) {
    return kindStyle
  }
  if (kind.deleted) {
    return kindStyle
  }
  const styles = kind.data?.styles || {}
  if (typeof styles.color === 'string') {
    kindStyle.color = styles.color
  }
  if (styles.bold) {
    kindStyle.fontWeight = 'bold'
  }
  if (styles.italic) {
    kindStyle.fontStyle = 'italic'
  }
  if (styles.underline) {
    kindStyle.textDecoration = 'underline'
  }
  return kindStyle
}

function isEventInModal(e: { target?: EventTarget | null }) {
  const el = e?.target as Element | null | undefined
  if (!el?.closest) {
    return false
  }
  return Boolean(el.closest('dialog') || el.closest('.modal'))
}

function isImageUrl(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false
  }
  try {
    const url = new URL(content)
    return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(url.pathname)
  } catch {
    return false
  }
}

function getCurrentKindStyles(
  kindId: string | undefined,
  kinds: KindMap | undefined,
): Record<string, unknown> | null {
  const kind = kindId ? kinds?.[kindId] : undefined
  if (!kind) {
    return null
  }
  if (kind.deleted) {
    return null
  }
  return kind.data?.styles || null
}

function GraphObject(props: GraphObjectProps) {
  const {
    object,
    layout,
    objectType,
    objectAttrs,
    showMetaDepth = 2,
    objectDetails,
    permissionsVisible,
    kinds,
    isDragging,
    editing: editingState,
    edit: editProp,
    stopDragging: stopDraggingProp,
    focusOne,
    updateMouse,
    openObjectDetails,
    startDragging: startDraggingProp,
    closeObjectDetails,
  } = props

  const objectRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef<HTMLDivElement>(null)
  const contentElRef = useRef<HTMLElement>(null)
  const editTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchStartPositionRef = useRef({ x: 0, y: 0 })
  const isDragRef = useRef(false)
  const isEditRef = useRef(false)
  const isDraggingRef = useRef(isDragging)
  isDraggingRef.current = isDragging
  const prevObjectRef = useRef(object)
  const prevKindStylesRef = useRef<Record<string, unknown> | null>(null)
  const addedToLayoutRef = useRef(false)
  const mountedOnceRef = useRef(false)
  const contentRef = useRef(object.data.content)

  const hiddenRef = useRef(true)
  const zoomValueRef = useRef(100)
  const [content, setContent] = useState(object.data.content)
  const [kindModalOpen, setKindModalOpen] = useState(false)

  const showMeta = useCallback(() => (object.depth || 0) <= showMetaDepth, [object.depth, showMetaDepth])

  const setStyle = useCallback(
    (newStyle: React.CSSProperties) => {
      const el = objectRef.current
      if (!el) return
      if (newStyle.left != null) el.style.left = String(newStyle.left)
      if (newStyle.top != null) el.style.top = String(newStyle.top)
      if (hiddenRef.current) {
        hiddenRef.current = false
        el.style.opacity = ''
      }
    },
    [],
  )

  const applyZoomToDOM = useCallback((z: number) => {
    const zEl = zoomRef.current
    if (!zEl) return
    const dz = z / 100
    zEl.style.transform = `scale(${dz})`
    const ox = Math.floor((zEl.offsetWidth * (dz - 1)) / 2)
    const oy = Math.floor((zEl.offsetHeight * (dz - 1)) / 2)
    zEl.style.marginLeft = `${ox}px`
    zEl.style.marginRight = `${ox}px`
    zEl.style.marginTop = `${oy}px`
    zEl.style.marginBottom = `${oy}px`
  }, [])

  const setZoom = useCallback((z: number) => {
    if (zoomValueRef.current === z) return
    zoomValueRef.current = z
    applyZoomToDOM(z)
  }, [applyZoomToDOM])

  const buildSimObj = useCallback(() => {
    const obj: Record<string, unknown> = {
      id: object.id,
      setStyle,
      setZoom,
      focused: object.focused || false,
      preserved: object.preserved || false,
      depth: object.depth,
      initialPosition: object.initialPosition,
      content: object.data.content,
      modified: object.modified,
      more: object.more,
      isMore: object.isMore,
    }

    if (objectType === 'edge') {
      const kindId = object?.data?.kind
      const kind = kindId ? kinds?.[kindId] : undefined
      if (kind) {
        const styles = kind.data?.styles || {}
        if (!kind.deleted && typeof styles.color === 'string') {
          obj.color = styles.color
        }
        obj.bold = !kind.deleted && Boolean(styles.bold)
        obj.direction = !kind.deleted ? (styles.edgeDirection ?? 'free') : 'free'
        obj.directionSoft = !kind.deleted && Boolean(styles.edgeDirectionSoft)
        obj.visible = kind.deleted || styles.edgeVisible !== false
        obj.spring = kind.deleted || styles.edgeSpring !== false
        obj.stiffness = kind.deleted ? undefined : styles.edgeStiffness
        obj.springLength = kind.deleted ? undefined : styles.edgeSpringLength
        obj.thickness = kind.deleted ? undefined : styles.edgeThickness
      }
    }

    each(objectAttrs, (attr: string) => {
      obj[attr] = (object as Record<string, unknown>)[attr] || (object.data as Record<string, unknown>)?.[attr]
    })
    return obj
  }, [object, objectType, objectAttrs, kinds, setStyle, setZoom])

  const setKind = useCallback(
    (kindId: string) => {
      const payload: Record<string, unknown> = {
        id: object.id,
        kind: kindId,
        content: object.data?.content,
      }
      if (objectType === 'edge') {
        payload.directionality = object.data?.directionality
      }
      editProp(payload)
    },
    [object.id, object.data, objectType, editProp],
  )

  const handleKindModalOpenChange = useCallback(
    (isOpen: boolean) => {
      setKindModalOpen((prev) => (prev === isOpen ? prev : isOpen))
    },
    [],
  )

  const onResize = useCallback(
    (width = 0, height = 0) => {
      if (!addedToLayoutRef.current) return
      layout.setSize(objectType, object.id, width, height)
    },
    [layout, objectType, object.id],
  )

  const currentPosition = useCallback(() => {
    const x = objectRef.current?.offsetLeft || 0
    const y = objectRef.current?.offsetTop || 0
    return { x, y }
  }, [])

  const movedEnoughForDrag = useCallback(() => {
    const x1 = touchStartPositionRef.current.x
    const y1 = touchStartPositionRef.current.y
    const cur = currentPosition()
    const dx = x1 - cur.x
    const dy = y1 - cur.y
    return Math.sqrt(dx * dx + dy * dy) > 20
  }, [currentPosition])

  const stopDragging = useCallback(
    (e?: DragEventShape) => {
      if (editTimeoutRef.current !== null) {
        clearTimeout(editTimeoutRef.current)
        editTimeoutRef.current = null
      }
      if (!isDraggingRef.current) {
        return
      }
      if (e?.cancelable) {
        e.preventDefault()
      }
      e?.stopPropagation?.()
      stopDraggingProp()
      if (isDragRef.current || isEditRef.current) {
        return
      }
      if (!object.focused) {
        focusOne({ id: object.isMoreFor || object.id })
      }
    },
    [object.focused, object.id, object.isMoreFor, stopDraggingProp, focusOne],
  )

  const startDragging = useCallback(
    (e: DragEventShape) => {
      if (e.cancelable) {
        e.preventDefault()
      }
      e.stopPropagation()
      updateMouse(e)
      const { x, y } = currentPosition()
      isDragRef.current = false
      isEditRef.current = false
      touchStartPositionRef.current = { x, y }
      if (!isEmbeddedReadOnly) {
        editTimeoutRef.current = setTimeout(() => {
          if (object.editing) {
            return
          }
          if (movedEnoughForDrag()) {
            return
          }
          isEditRef.current = true
          stopDragging()
          focusOne({ id: object.isMoreFor || object.id })
          openObjectDetails()
        }, 750)
      }
      layout.setDragOffset(x, y)
      startDraggingProp({ id: object.id })
    },
    [
      updateMouse,
      currentPosition,
      object.editing,
      object.id,
      object.isMoreFor,
      movedEnoughForDrag,
      stopDragging,
      focusOne,
      openObjectDetails,
      layout,
      startDraggingProp,
    ],
  )

  const onMouseMove = useCallback(
    (e: DragEventShape) => {
      if (!isDragging) {
        return
      }
      if (e.cancelable) {
        e.preventDefault()
      }
      if (movedEnoughForDrag()) {
        isDragRef.current = true
        if (editTimeoutRef.current !== null) {
          clearTimeout(editTimeoutRef.current)
          editTimeoutRef.current = null
        }
      }
    },
    [isDragging, movedEnoughForDrag],
  )

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (kindModalOpen) return
      if (isEventInModal(e)) return
      startDragging(e)
    },
    [kindModalOpen, startDragging],
  )

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (kindModalOpen) return
      if (isEventInModal(e)) return
      if (e.button && e.button !== 0) return
      startDragging(e)
    },
    [kindModalOpen, startDragging],
  )

  const onMouseUp = useCallback(
    (e: DragEventShape) => {
      stopDragging(e)
    },
    [stopDragging],
  )

  const contentMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      if (object.editing) {
        e.stopPropagation()
      }
    },
    [object.editing],
  )

  const contentMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      if (object.editing) {
        e.stopPropagation()
      }
    },
    [object.editing],
  )

  const onContentChanged = useCallback((e: { target: { value: string } }) => {
    contentRef.current = e.target.value
    setContent(e.target.value)
  }, [])

  const stopEditing = useCallback(
    (cancel?: boolean) => {
      closeObjectDetails(!cancel)
    },
    [closeObjectDetails],
  )

  const onKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!object.editing) return
      const key = e.keyCode || e.which
      switch (key) {
        case 13:
        case 10:
          if (!e.shiftKey) {
            stopEditing()
            e.preventDefault()
          }
          break
        case 27:
          stopEditing(true)
          e.preventDefault()
          break
        default:
          break
      }
    },
    [object.editing, stopEditing],
  )

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!object.editing) return
      const key = e.keyCode || e.which
      switch (key) {
        case 27:
          stopEditing(true)
          e.preventDefault()
          break
        default:
          break
      }
    },
    [object.editing, stopEditing],
  )

  const getOffsetX = useCallback(() => {
    if (!zoomRef.current?.offsetWidth) return 0
    const z = zoomValueRef.current / 100
    return Math.floor((zoomRef.current.offsetWidth * (z - 1)) / 2)
  }, [])

  const getOffsetY = useCallback(() => {
    if (!zoomRef.current?.offsetHeight) return 0
    const z = zoomValueRef.current / 100
    return Math.floor((zoomRef.current.offsetHeight * (z - 1)) / 2)
  }, [])

  // componentDidMount: add to layout
  useLayoutEffect(() => {
    setContent(object.data.content)
    const el = objectRef.current
    const simObj = buildSimObj()
    // On HMR re-mount, DOM persists but layout node was removed.
    // Restore position from inline style and size from DOM measurements,
    // since ResizeObserver won't fire when the element size hasn't changed.
    if (mountedOnceRef.current && el) {
      const left = parseFloat(el.style.left)
      const top = parseFloat(el.style.top)
      if (!isNaN(left) && !isNaN(top)) {
        simObj.initialPosition = { x: left, y: top }
      }
    }
    layout.add(objectType, simObj)
    addedToLayoutRef.current = true
    if (mountedOnceRef.current && el && el.offsetWidth > 0) {
      layout.setSize(objectType, object.id, el.offsetWidth, el.offsetHeight)
    }
    mountedOnceRef.current = true
    return () => {
      addedToLayoutRef.current = false
      layout.remove(objectType, object.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate: respond to object prop changes
  useLayoutEffect(() => {
    const prev = prevObjectRef.current
    prevObjectRef.current = object

    const currentKindStyles = getCurrentKindStyles(object?.data?.kind, kinds)
    const prevKindStyles = prevKindStylesRef.current
    const kindStylesEqual = isEqual(currentKindStyles, prevKindStyles)
    prevKindStylesRef.current = currentKindStyles

    if (isEqual(object, prev) && kindStylesEqual) {
      return
    }
    if (object.editing && !kindModalOpen) {
      contentElRef.current?.focus()
    }
    if (!object.editing) {
      if (prev.editing && editingState.save && contentRef.current !== object.data.content) {
        const cleaned = contentRef.current.replace(/(<br>)+$/, '')
        editProp({
          id: object.id,
          content: cleaned,
        })
      } else {
        contentRef.current = object.data.content
        setContent(object.data.content)
      }
    }
    layout.update(objectType, buildSimObj())
  }, [object, kindModalOpen, editingState.save, editProp, layout, objectType, buildSimObj, kinds])

  // Cleanup edit timeout on unmount
  useEffect(() => {
    return () => {
      if (editTimeoutRef.current !== null) {
        clearTimeout(editTimeoutRef.current)
      }
    }
  }, [])

  // Register touch listeners as non-passive so preventDefault works during drag
  useEffect(() => {
    const el = objectRef.current
    if (!el) return
    const touchStartHandler = (e: Event) => onTouchStart(e as unknown as React.TouchEvent<HTMLDivElement>)
    const touchMoveHandler = (e: Event) => onMouseMove(e as unknown as React.MouseEvent<HTMLDivElement> & React.TouchEvent<HTMLDivElement>)
    el.addEventListener('touchstart', touchStartHandler, { passive: false })
    el.addEventListener('touchmove', touchMoveHandler, { passive: false })
    return () => {
      el.removeEventListener('touchstart', touchStartHandler)
      el.removeEventListener('touchmove', touchMoveHandler)
    }
  }, [onTouchStart, onMouseMove])

  const { focused, pinned, selected, editing, id, data, depth, more } = object
  const creator = toPermissionUser(data?.creator)
  const readers = toPermissionRecord(data?.readers)
  const writers = toPermissionRecord(data?.writers)
  let displayContent = content
  const contentAutoCapitalize = objectType === 'edge' ? 'off' : undefined
  if (displayContent == null) {
    displayContent = ''
  }
  const depthLevel = depth || 0
  const shouldShowEmptyContentPlaceholder =
    objectType === 'node' || (!isEmbeddedReadOnly && depthLevel <= 2)
  if (!editing && displayContent === '' && shouldShowEmptyContentPlaceholder) {
    displayContent = '•'
  }
  const moreString = more ? `+${more} more` : undefined

  const kindId = object?.data?.kind
  const kindStyle = getKindStyles(kindId, kinds)
  const footerObject = {
    id: object.id,
    modified: typeof object.modified === 'number' ? object.modified : 0,
    data: {
      readers: readers || {},
      writers: writers || {},
      ...(creator ? { creator } : {}),
      ...(object.data?.content != null ? { content: String(object.data.content) } : {}),
      ...(kindId ? { kind: kindId } : {}),
    },
  }
  const footerKinds = kinds

  return (
    <div
      className={cn(objectType, `graph-object`, `depth-${depthLevel}`, {
        focused,
        pinned,
        selected,
        editing,
        'drop-target': props.isDropTarget,
      })}
      style={{ position: 'absolute', opacity: 0 }}
      onMouseMove={onMouseMove}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchEnd={onMouseUp}
      onTouchCancel={onMouseUp}
      ref={objectRef}
      data-graph-type={objectType}
      data-graph-id={id}
    >
      <div className="content-wrapper">
        <div
          className="zoom"
          ref={zoomRef}
          style={{
            transition: 'transform 0.3s linear',
          }}
        >
          <div className="content-row">
            {permissionsVisible && showMeta() && !objectDetails.isOpen ? (
              <Permissions
                {...(creator ? { creator } : {})}
                {...(readers ? { readers } : {})}
                {...(writers ? { writers } : {})}
              />
            ) : null}
            {!editing && isImageUrl(displayContent) ? (
              <img
                src={displayContent}
                alt="Object content"
                className="content"
                style={kindStyle}
              />
            ) : (
              <ContentEditable
                innerRef={contentElRef}
                onBlur={() => {
                  if (object.editing && !kindModalOpen) {
                    contentElRef.current?.focus()
                  }
                }}
                html={displayContent}
                className={cn('content', { editing })}
                style={kindStyle}
                autoCapitalize={contentAutoCapitalize}
                disabled={!editing}
                onChange={onContentChanged}
                onKeyPress={onKeyPress}
                onKeyUp={onKeyUp}
                onMouseDown={contentMouseDown}
                onTouchStart={contentMouseDown}
                onMouseUp={contentMouseUp}
                onTouchEnd={contentMouseUp}
                onTouchCancel={contentMouseUp}
              />
            )}
          </div>
          <FooterMeta
            object={footerObject}
            showMeta={showMeta()}
            isOpen={focused != null && objectDetails.isOpen}
            permissionsVisible={permissionsVisible}
            {...(footerKinds ? { kinds: footerKinds } : {})}
            {...(editing != null ? { isEditing: editing } : {})}
            onSelectKind={setKind}
            onKindModalOpenChange={handleKindModalOpenChange}
            objectType={objectType}
            {...(props.editOneKind ? { editOneKind: props.editOneKind } : {})}
            {...(props.createOneKind ? { createOneKind: props.createOneKind } : {})}
          />
          <div className={cn('more-edges')}>{moreString}</div>
        </div>
        <div className="indicators">{pinned ? <FaThumbtack className={cn('pinned-icon')} /> : null}</div>
      </div>
      <ResizeHandler targetRef={objectRef} onResize={onResize} />
    </div>
  )
}

export default GraphObject
